import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Settings, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import {
  ModuleName,
  SidebarConfig,
  SidebarItem,
} from '../../../types/navigation';
import { UserRole } from '../../../types/users';

interface SidebarProps {
  variant: UserRole;
  activeModule: ModuleName;
  expandedItems: Record<string, boolean>;
  setExpandedItems: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  config: SidebarItem[];
  collapsed: boolean;           
  onCollapse: (v: boolean) => void;
  visibleModules?: { id: string; label: string }[];
  onModuleSelect?: (moduleId: ModuleName) => void;
}

const Sidebar = ({
  variant,
  activeModule,
  expandedItems,
  setExpandedItems,
  config,
  collapsed,
  onCollapse,
  visibleModules,
  onModuleSelect,
}: SidebarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  //const [collapsed, setCollapsed] = useState(false); // ✅ collapse state

  const routeModuleMap = {
    pmo: 'Project Management' as ModuleName,
    hrms: 'HRMS' as ModuleName,
    crm: 'CRM' as ModuleName,
    finance: 'Finance' as ModuleName,
  };

  const getCurrentModuleFromRoute = (): ModuleName => {
    const currentPath = location.pathname;
    for (const [routeSegment, moduleName] of Object.entries(routeModuleMap)) {
      if (currentPath.includes(routeSegment)) return moduleName;
    }
    return 'Project Management';
  };

  const currentActiveModule = getCurrentModuleFromRoute();

  const toggleExpand = (id: string, currentlyActive?: boolean) => {
    setExpandedItems((prev) => {
      const isCurrentlyExpanded = prev[id] ?? currentlyActive ?? false;
      return { ...prev, [id]: !isCurrentlyExpanded };
    });
  };

  const handleItemClick = (item: SidebarItem) => {
    if (item.path && location.pathname !== item.path) navigate(item.path);
    if (item.children) toggleExpand(item.id, isActive(item.path, item.children));
  };

  const isActive = (path?: string, children?: SidebarItem[]): boolean => {
    const currentPath = location.pathname;
    
    // Check if any child is active first
    const isChildActive = children?.some((child) => {
      if (!child.path) return false;
      if (currentPath === child.path) return true;
      const childSegments = child.path.split('/').filter(Boolean);
      const currentSegments = currentPath.split('/').filter(Boolean);
      return childSegments.length > 0 && childSegments.every(
        (segment, index) => index < currentSegments.length && segment === currentSegments[index]
      );
    }) || false;

    if (isChildActive) return true;
    if (!path) return false;

    const isParentActive = currentPath === path;
    const pathSegments = path.split('/').filter(Boolean);
    const currentSegments = currentPath.split('/').filter(Boolean);
    const isParentOfCurrentPath = pathSegments.length > 0 && pathSegments.every(
      (segment, index) => index < currentSegments.length && segment === currentSegments[index]
    );

    return isParentActive || isParentOfCurrentPath;
  };

  const getActiveStyles = (isActiveItem: boolean, isChild: boolean = false) => {
    if (currentActiveModule === 'Project Management') {
      if (isChild) {
        return isActiveItem ? 'text-[#8B5CF6]' : 'text-gray-400 hover:text-neutral';
      } else {
        return isActiveItem
          ? 'bg-primary-intermediate text-[#8B5CF6]'
          : 'text-gray-400 hover:bg-primary-intermediate hover:text-white';
      }
    } else {
      if (isChild) {
        return isActiveItem ? 'text-[#8B5CF6]' : 'text-gray-400 hover:text-neutral';
      } else {
        return isActiveItem
          ? 'bg-primary-intermediate text-[#8B5CF6]'
          : 'text-gray-400 hover:bg-primary-intermediate hover:text-white';
      }
    }
  };

  const renderSidebarItem = (item: SidebarItem) => {
    if (item.id === 'my-workspace' && (variant === 'ORG_OWNER' || variant === 'ORG_ADMIN')) {
      return null;
    }

    if (item.type === 'group') {
      return (
        <div key={item.id} className="mt-6 mb-2">
          {!collapsed && (
            <div className="px-5 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              {item.label}
            </div>
          )}
          {collapsed && (
            <div className="w-8 mx-auto border-t border-gray-600 mb-4 mt-4"></div>
          )}
          <div className="space-y-0">
            {item.children?.map((child) => renderSidebarItem(child))}
          </div>
        </div>
      );
    }

    const isParentActive = isActive(item.path, item.children);
    return (
      <div key={item.id} className='mb-1 px-2'>
        <div
          title={collapsed ? item.label : undefined} // ✅ tooltip when collapsed
          className={`group flex items-center px-3 py-3 rounded-lg cursor-pointer transition-colors ${getActiveStyles(
            isParentActive
          )} ${collapsed ? 'justify-center' : ''}`}
          onClick={() => handleItemClick(item)}
        >
          {item.icon && (
            <span className={`transition-colors duration-200 ${collapsed ? '' : 'mr-3'}`}>{item.icon}</span>
          )}
          {/* ✅ hide label and chevron when collapsed */}
          {!collapsed && (
            <>
              <span className='flex-1 truncate min-w-0' title={item.label}>{item.label}</span>
              {item.children &&
                (expandedItems[item.id] ?? isParentActive ? (
                  <ChevronDown size={16} className="flex-shrink-0" />
                ) : (
                  <ChevronRight size={16} className="flex-shrink-0" />
                ))}
            </>
          )}
        </div>

        {/* ✅ hide children when collapsed */}
        {!collapsed && (expandedItems[item.id] ?? isParentActive) && (
          <div className='ml-8 mt-1 space-y-1'>
            {item.children?.map((child) => (
              <div
                key={child.id}
                className={`flex gap-1 items-center px-3 py-2 rounded text-sm cursor-pointer transition-colors ${getActiveStyles(
                  isActive(child.path),
                  true
                )}`}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(child.path || '#');
                }}
              >
                {child.icon && <span className='mr-3 flex-shrink-0'>{child.icon}</span>}
                <span className='truncate min-w-0 flex-1' title={child.label}>{child.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    // ✅ width transitions between collapsed (w-16) and expanded (w-64)
    <aside className={`${collapsed ? 'w-16' : 'w-64'} bg-primary-light h-full min-991:pt-[70px] border-r border-primary-subtle flex-shrink-0 overflow-y-auto transition-all duration-300`}>
      <div className='py-2 flex flex-col h-full'>
      
        {visibleModules && visibleModules.length > 0 && (
          <div className="min-991:hidden px-4 py-4 border-b border-primary-subtle mb-2">
            <div className="relative">
              <select
                value={currentActiveModule}
                onChange={(e) => onModuleSelect?.(e.target.value as ModuleName)}
                className="w-full appearance-none bg-[#13161F] border border-[#2A2F3D] text-white rounded-lg px-4 py-2.5 text-sm font-medium focus:outline-none focus:ring-1 focus:ring-color-accent pr-8"
              >
                {visibleModules.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
        )}

        {/* ✅ header with collapse toggle */}
        <div className='px-4 py-2 hidden min-991:flex justify-between items-center'>
          {!collapsed && (
            <h2 className='font-semibold text-lg truncate'>{currentActiveModule}</h2>
          )}
          <button
            onClick={() => onCollapse(!collapsed)}
            className={`text-gray-400 hover:text-white transition-colors ${collapsed ? 'mx-auto' : ''}`}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed
              ? <PanelLeftOpen size={18} />
              : <PanelLeftClose size={18} />
            }
          </button>
        </div>

        <nav className={`${currentActiveModule === 'Project Management' ? 'mt-6' : 'mt-2'} flex-1`}>
          {config?.map(renderSidebarItem)}
        </nav>

        {/* {['PMO_ADMIN', 'ORG_OWNER', 'ORG_ADMIN'].includes(variant) && (
          <div className='mt-auto p-4 border-t border-gray-800'>
            <div
              className={`flex items-center text-gray-400 hover:text-white cursor-pointer ${collapsed ? 'justify-center' : ''}`}
              onClick={() => navigate('/')}
              title={collapsed ? 'Settings' : undefined}
            >
              <Settings size={20} className={collapsed ? '' : 'mr-3'} />
              {!collapsed && <span>Settings</span>}
            </div>
          </div>
        )} */}
      </div>
    </aside>
  );
};

export default Sidebar;